import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDownIcon, CheckIcon, TruckIcon } from 'lucide-react';
import { requestOffer } from '../../utils/offerBus';
import { BUILDER_ID, builderPrices, builderSteps, essentialIncludes } from '../../data/builder';
import { useBuilder } from '../../hooks/useBuilder';
import { useElementInView } from '../../hooks/useElementInView';
import { useTrackOnView } from '../../hooks/useTrackOnView';
import { money } from '../QuantityStepper';
import { BuilderVisual } from './BuilderVisual';
import { BuilderStepPanel } from './BuilderStepPanel';
import { BuilderSummary, BuilderSummaryBar } from './BuilderSummary';
import { CompleteUpgradeNotice } from './CompleteUpgradeNotice';
import { HardCaseDownsell } from './HardCaseDownsell';
export function BuildYourX1() {
  const builder = useBuilder();
  const reduce = useReducedMotion();
  const viewRef = useTrackOnView<HTMLElement>('build_your_x1_view');
  const inView = useElementInView(BUILDER_ID, '-30% 0px -30% 0px');
  const {
    state,
    step
  } = builder;
  const index = builderSteps.findIndex(item => item.id === step);
  const [pulse, setPulse] = useState(0);

  // Any "Build your X1" link: let the smooth scroll land, then briefly highlight the builder.
  useEffect(() => {
    let timer = 0;
    const onClick = (event: MouseEvent) => {
      const link = (event.target as HTMLElement | null)?.closest('a');
      const href = link?.getAttribute('href');
      if (!link || href !== `#${BUILDER_ID}` && href !== '#offer') return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setPulse(count => count + 1), 750);
    };
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('click', onClick);
      window.clearTimeout(timer);
    };
  }, []);
  const stepDone = (id: string) => id === 'lens' && step !== 'lens' || id === 'lensSystem' && (state.fullLens || state.complete) || id === 'case' && (state.hardCase || state.complete) || id === 'care' && state.careKit || id === 'rx' && state.rx > 0 || id === 'protection' && state.protection !== 'basic';
  return <section ref={viewRef} id={BUILDER_ID} data-builder="true" aria-labelledby="builder-heading" className="relative scroll-mt-24 border-b border-white/10 bg-ink-950" data-id="element-262" data-mp="3ovqyay0bxlpr">
      {pulse > 0 && <motion.span key={pulse} aria-hidden="true" className="pointer-events-none absolute inset-0 z-10 border-2 border-grit-500" initial={{
      opacity: 0
    }} animate={{
      opacity: reduce ? 0 : [0, 1, 0]
    }} transition={{
      duration: 0.3,
      times: [0, 0.35, 1],
      ease: 'easeOut'
    }} data-id="element-263" data-mp="3fwdvupamhxej" />}
      <div className="mx-auto max-w-[1400px] px-4 py-12 sm:px-6 lg:px-8 lg:py-16" data-id="element-264" data-mp="314lj8l27qxgi">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between" data-id="element-265" data-mp="2w9fy3ogmxmth">
          <div data-id="element-266" data-mp="2s6jlqfhoumxz">
            <p className="font-display text-xl font-extrabold uppercase tracking-wide text-grit-500" data-id="element-267" data-mp="2s844z8yjyjgj">
              Your lens. Your setup. Your way.
            </p>
            <h2 id="builder-heading" className="grit-headline mt-1 text-[clamp(2.5rem,9vw,5rem)] font-extrabold uppercase text-offwhite" data-id="element-268" data-mp="1bnzni9z1gisw">
              Build your <span className="text-grit-500" data-id="element-269" data-mp="1lngr8luqcmm8">X1.</span>
            </h2>
            <p className="mt-3 text-lg font-medium text-steel-300" data-id="element-270" data-mp="1lle6ag8fpjod">
              Choose your lens. Add only what you need. Watch it come together.
            </p>
            <a href="#pack-single" onClick={event => {
            event.preventDefault();
            requestOffer('single');
          }} className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wide text-steel-300 underline-offset-4 hover:text-grit-500 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-271" data-mp="szuplj52bs20">
              Want a ready-made kit? Complete Systems &amp; deals below
              <ArrowDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-272" data-mp="2by0riw8yoy36" />
            </a>
          </div>
          <div className="lg:text-right" data-id="element-273" data-mp="27xgurtsavwhk">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-274" data-mp="2lnaksffjkmsv">GritVue X1 Essential</p>
            <p className="mt-1 font-display text-3xl font-extrabold uppercase leading-none text-offwhite" data-id="element-275" data-mp="2gw2aziaauzf6">
              Starting at <span className="text-grit-500" data-id="element-276" data-mp="2vkf9alwv0ucu">{money(builderPrices.essential)}</span>
            </p>
            <p className="mt-2 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide text-steel-300 lg:justify-end" data-id="element-277" data-mp="2qlc3v0cywm4c">
              <TruckIcon className="h-4 w-4 text-grit-500" aria-hidden="true" data-id="element-278" data-mp="34k2ap7pla05w" />
              Free U.S. shipping
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:gap-10" data-id="element-279" data-mp="1a96r7acv90oc">
          {/* The product stays anchored in view while the build changes it */}
          <div className="sticky top-[5.25rem] z-20 -mx-4 self-start bg-ink-950 px-4 pb-3 sm:-mx-6 sm:px-6 lg:top-24 lg:mx-0 lg:px-0 lg:pb-0" data-id="element-280" data-mp="3gwe4ojkw9xz0">
            <BuilderVisual state={state} animateLenses={builder.step === 'lensSystem'} data-id="element-281" data-mp="3lufo55m8fzb0" />
            <ul className="mt-4 hidden gap-x-6 gap-y-1.5 sm:grid sm:grid-cols-3" data-id="element-282" data-mp="384vpobzopo7i">
              {essentialIncludes.map(item => <li key={item} className="flex items-start gap-2 text-[13px] text-steel-400" data-id="element-283" data-mp="3c3ut1s5rpcwu">
                  <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-284" data-mp="2y4bnn74ccfo8" />
                  {item}
                </li>)}
            </ul>
          </div>

          <div id="builder-panel" className="grid scroll-mt-[26rem] content-start gap-4 lg:scroll-mt-28" data-id="element-285" data-mp="2p6swdl03t53w">
            {/* Mobile: one readable step label and a thin progress bar */}
            <div className="lg:hidden" aria-live="polite" data-id="element-286" data-mp="nf6d65mgeehz">
              <p className="flex items-baseline justify-between text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-287" data-mp="rf6qto1qalnz">
                <span data-id="element-288" data-mp="20dgaf044ol24">
                  Step {index + 1} of {builderSteps.length}
                </span>
                <span className="font-display text-lg tracking-wide text-offwhite" data-id="element-289" data-mp="1vciuyvai654b">{builderSteps[index]?.label}</span>
              </p>
              <div className="mt-2 h-1 w-full bg-white/10" data-id="element-290" data-mp="1rdtayjuylpbp">
                <motion.div className="h-full origin-left bg-grit-500" animate={{
                scaleX: (index + 1) / builderSteps.length
              }} transition={{
                duration: reduce ? 0 : 0.25,
                ease: [0.23, 1, 0.32, 1]
              }} data-id="element-291" data-mp="1mdnlnac5wews" />
              </div>
            </div>

            {/* Desktop: full step navigation */}
            <nav aria-label="Build steps" className="hidden lg:block" data-id="element-292" data-mp="25bht3jxn6id2">
              <ol className="grid grid-cols-7 border border-white/10" data-id="element-293" data-mp="25b8521w5qla0">
                {builderSteps.map((item, i) => {
                const current = item.id === step;
                const done = stepDone(item.id);
                return <li key={item.id} className={i > 0 ? 'border-l border-white/10' : ''} data-id="element-294" data-mp="35c3hgm488c8x" data-mpi={i}>
                      <button type="button" onClick={() => builder.goToStep(item.id)} aria-current={current ? 'step' : undefined} className={`flex h-full w-full flex-col items-center gap-1 px-1 py-2.5 transition-colors duration-150 ease-grit focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-grit-500 ${current ? 'bg-grit-500 text-ink-950' : 'text-steel-300 hover:bg-white/[0.04] hover:text-offwhite'}`} data-id="element-295" data-mp="3a9v8ldx1ruc2">
                        <span className={`flex h-5 w-5 items-center justify-center text-[11px] font-extrabold ${current ? '' : done ? 'bg-grit-500 text-ink-950' : 'border border-white/25'}`} data-id="element-296" data-mp="6txwgzfbscb3">
                          {done && !current ? <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-297" data-mp="11401w9ka40a2" /> : i + 1}
                        </span>
                        <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.1em]" data-id="element-298" data-mp="w8kmhd6mhwbj">{item.label}</span>
                      </button>
                    </li>;
              })}
              </ol>
            </nav>

            <CompleteUpgradeNotice hint={builder.hint} complete={state.complete} onUpgrade={builder.upgradeToComplete} onRevert={builder.revertToEssential} data-id="element-299" data-mp="ifuzb5fcylcu" />

            <BuilderStepPanel builder={builder} data-id="element-300" data-mp="s5xsp6qe61bp" />

            {step !== 'review' && <div className="hidden lg:block" data-id="element-301" data-mp="ncbps6a15bt3">
                <BuilderSummary builder={builder} data-id="element-302" data-mp="3vtga2kxt3v19" />
              </div>}
          </div>
        </div>

        {/* Keep people scrolling: the ready-made kits and pack deals are right below */}
        <div className="mt-12 flex flex-col gap-5 border-t-4 border-grit-500 bg-ink-900 p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between" data-id="element-303" data-mp="26cuce4b2n6b0">
          <div data-id="element-304" data-mp="2b4vqlvrsv73o">
            <p className="font-display text-3xl font-extrabold uppercase leading-none text-offwhite sm:text-4xl" data-id="element-305" data-mp="2b5dthb8ywjkk">
              Don&apos;t want to build it? <span className="text-grit-500" data-id="element-306" data-mp="21cpy8kxmynny">Keep scrolling.</span>
            </p>
            <p className="mt-3 max-w-2xl text-[15px] text-steel-300" data-id="element-307" data-mp="2p3bvria0kpjp">
              Ready-made Complete Systems with all 3 lenses and the hard case, plus multi-pack deals with Lifetime
              replacement on every X1. Free U.S. shipping on every order.
            </p>
          </div>
          <a href="#pack-single" onClick={event => {
          event.preventDefault();
          requestOffer('single');
        }} className="inline-flex shrink-0 items-center justify-center gap-2 border-2 border-grit-500 px-6 py-4 font-display text-lg font-extrabold uppercase tracking-wide text-grit-500 transition-[background-color,color,transform] duration-150 ease-grit hover:bg-grit-500 hover:text-ink-950 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-308" data-mp="2k3z6kqvo76dy">
            See Complete Systems &amp; deals
            <ArrowDownIcon className="h-5 w-5" aria-hidden="true" data-id="element-309" data-mp="ixngc149onte" />
          </a>
        </div>
      </div>

      <BuilderSummaryBar builder={builder} visible={inView} data-id="element-310" data-mp="4xuqs94h8dn0" />
      <HardCaseDownsell open={builder.downsellOpen} total={builder.total} onAccept={builder.acceptDownsell} onDecline={builder.declineDownsell} onClose={builder.closeDownsell} data-id="element-311" data-mp="50qru6bmfifo" />
    </section>;
}